import React, { memo } from 'react';
import { createPortal } from 'react-dom';
import { MissionCardBody } from '../../../shared/CardComponents';
import { STATUS_CONFIG, CATEGORY_COLORS } from '../../TaskNode/constants';
import { getPrimaryTag } from '../../../hooks/useTagActions';
import './quick-view.css';

// Offset from cursor
const CURSOR_OFFSET_X = 16;
const CURSOR_OFFSET_Y = 8;

// Preview card dimensions (full-size card)
const PREVIEW_WIDTH = 288;
// Estimated height for positioning calculations
const ESTIMATED_HEIGHT = 200;

/**
 * QuickViewPreview Component
 * 
 * Shows a full-size preview of a card when hovering over a zoomed-out node.
 * The preview follows the cursor and provides a readable view of card content.
 * 
 * PERFORMANCE: Position is calculated synchronously from mousePosition prop.
 * No useEffect, no useState for position - instant positioning.
 */
const QuickViewPreview = memo(({
  task,
  statuses = [],
  allCategories = [],
  allTags = [],
  nestedSprint = null,
  cardDisplaySettings = {},
  mousePosition,
}) => {
  if (!task || !mousePosition) return null;
  
  // Get status config for styling
  const statusConfig = STATUS_CONFIG[task.status] || STATUS_CONFIG.pending;
  
  // Get primary tag for category styling
  const primaryTagName = getPrimaryTag(task);
  const availableTags = allTags.length > 0 ? allTags : allCategories;
  const primaryTagObject = primaryTagName 
    ? availableTags.find(t => t.name === primaryTagName) || { name: primaryTagName, color: CATEGORY_COLORS[0] }
    : null;

  // Calculate position synchronously - no state, no effect
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  
  let x = mousePosition.x + CURSOR_OFFSET_X;
  let y = mousePosition.y + CURSOR_OFFSET_Y;
  
  // Keep within right edge - flip to left of cursor if needed
  if (x + PREVIEW_WIDTH > viewportWidth - 16) {
    x = mousePosition.x - PREVIEW_WIDTH - CURSOR_OFFSET_X;
  }
  
  // Keep within bottom edge
  if (y + ESTIMATED_HEIGHT > viewportHeight - 16) {
    y = viewportHeight - ESTIMATED_HEIGHT - 16;
  }
  
  // Keep within left edge
  if (x < 16) {
    x = 16;
  }
  
  // Keep within top edge
  if (y < 16) {
    y = 16;
  }

  const isBlocked = task.status === 'blocked';
  const isCompleted = task.status === 'completed' || (nestedSprint && nestedSprint.progress === 100);

  return createPortal(
    <div 
      className={`quick-view-preview ${primaryTagObject ? 'quick-view-preview--has-category' : ''} ${isCompleted ? 'quick-view-preview--completed' : ''}`}
      style={{
        left: x,
        top: y,
        width: PREVIEW_WIDTH,
        '--status-color': statusConfig.color,
        '--status-bg': statusConfig.bg,
        '--category-color': primaryTagObject?.color || 'transparent',
        '--category-bg': primaryTagObject ? `${primaryTagObject.color}18` : 'transparent',
      }}
    >
      <MissionCardBody
        task={task}
        category={primaryTagObject}
        allTags={availableTags}
        statuses={statuses}
        nestedSprint={nestedSprint}
        isBlocked={isBlocked}
        cardDisplaySettings={cardDisplaySettings}
        // No editing in preview - read-only
        onRename={() => {}}
        onIconChange={() => {}}
        onStatusChange={() => {}}
        onAddTag={() => {}}
        onRemoveTag={() => {}}
        onSetPrimaryTag={() => {}}
        onRenameTag={() => {}}
        onDeleteTag={() => {}}
        onOpenDetails={() => {}}
        onOpenNestedSprint={() => {}}
        onRemoveNestedSprint={() => {}}
        onAddStatus={() => {}}
        onDeleteStatus={() => {}}
        onRenameStatus={() => {}}
      />
    </div>,
    document.body
  );
});

QuickViewPreview.displayName = 'QuickViewPreview';

export default QuickViewPreview;
